import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Check } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxneW0lMjBhdGhsZXRlJTIwZGFya3xlbnwwfHx8fDE3ODE2ODMzMzZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register, formatApiError } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);

  // Pre-fill saved credentials on this device
  useEffect(() => {
    const savedEmail = localStorage.getItem("cm_saved_email");
    const savedPassword = localStorage.getItem("cm_saved_password");
    if (savedEmail) {
      setEmail(savedEmail);
      setRemember(true);
    }
    if (savedPassword) setPassword(savedPassword);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password, inviteCode);
      // Remember credentials on this device (personal use)
      if (remember) {
        localStorage.setItem("cm_saved_email", email);
        localStorage.setItem("cm_saved_password", password);
      } else {
        localStorage.removeItem("cm_saved_email");
        localStorage.removeItem("cm_saved_password");
      }
    } catch (err) {
      setError(err?.message || formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-md min-h-screen flex flex-col bg-[#0a0a0a]">
      <div className="relative h-72 w-full overflow-hidden">
        <img src={HERO} alt="" className="h-full w-full object-cover grayscale" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-[#0a0a0a]" />
        <div className="absolute bottom-6 left-5 right-5">
          <p className="font-head text-sm uppercase tracking-[0.3em] text-[#fa003f]">Solvap</p>
          <h1 className="font-head text-5xl font-black uppercase tracking-tight text-white leading-[0.95]">
            Mobility
            <br />
            Program
          </h1>
          <a
            href="https://instagram.com/_solvap_"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram di Solvap"
            className="mt-2 inline-block text-zinc-400 hover:text-zinc-300 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none" />
            </svg>
          </a>
        </div>
      </div>

      <div className="flex-1 px-5 pt-8">
        <div className="mb-6 flex border border-zinc-800">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`auth-tab-${m}`}
              onClick={() => {
                setMode(m);
                setError("");
              }}
              className={`flex-1 py-3 font-head text-lg font-bold uppercase tracking-wide transition-colors ${
                mode === m ? "bg-[#fa003f] text-white" : "text-zinc-500"
              }`}
            >
              {m === "login" ? "Accedi" : "Registrati"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <>
              <input
                data-testid="auth-name-input"
                type="text"
                placeholder="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full border border-zinc-800 bg-[#141414] px-4 h-14 text-white placeholder-zinc-600 outline-none focus:border-[#fa003f]"
              />
              <input
                data-testid="auth-invite-input"
                type="text"
                placeholder="Codice d'invito"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                required
                className="w-full border border-zinc-800 bg-[#141414] px-4 h-14 text-white placeholder-zinc-600 outline-none focus:border-[#fa003f]"
              />
            </>
          )}
          <input
            data-testid="auth-email-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full border border-zinc-800 bg-[#141414] px-4 h-14 text-white placeholder-zinc-600 outline-none focus:border-[#fa003f]"
          />
          <input
            data-testid="auth-password-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full border border-zinc-800 bg-[#141414] px-4 h-14 text-white placeholder-zinc-600 outline-none focus:border-[#fa003f]"
          />

          {error && (
            <p data-testid="auth-error" className="text-sm text-[#fa003f] font-medium">
              {error}
            </p>
          )}

          <button
            type="button"
            data-testid="remember-checkbox"
            onClick={() => setRemember((r) => !r)}
            className="flex items-center gap-3 py-1 active:scale-[0.99] transition-transform"
          >
            <span
              className={`flex h-6 w-6 items-center justify-center border-2 transition-colors ${
                remember ? "border-[#fa003f] bg-[#fa003f]" : "border-zinc-700 bg-transparent"
              }`}
            >
              {remember && <Check size={16} strokeWidth={3.5} className="text-white" />}
            </span>
            <span className="text-sm text-zinc-400">Ricordami su questo dispositivo</span>
          </button>

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-[#fa003f] font-head text-xl font-bold uppercase tracking-wide text-white active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={20} className="animate-spin" />}
            {mode === "login" ? "Inizia" : "Crea account"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-zinc-600 leading-relaxed px-4">
          Traccia ogni esercizio, segui i progressi e completa il tuo percorso di mobilità.
        </p>
      </div>
    </div>
  );
}
